import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import ContactModal from '../components/ContactModal';
import VideoModal from '../components/VideoModal';
import AppCarousel from '../components/AppCarousel';
import {
    CheckCircle2,
    ArrowRight,
    Database,
    Sparkles,
    Bot,
    Play,
    Menu,
    X,
    Monitor,
    Smartphone,
    Terminal,
    Zap,
    Shield,
    GitBranch,
    FileCheck,
    Clock,
    TrendingUp,
} from 'lucide-react';

const Landing: React.FC = () => {
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [contactSource, setContactSource] = useState('landing_page');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const openContactModal = (source: string) => {
        setContactSource(source);
        setIsContactModalOpen(true);
        setIsMobileMenuOpen(false);
    };


    return (
        <div className="min-h-screen bg-background-dark text-white overflow-x-hidden font-sans">
            <style>
                {`
                .font-montserrat { font-family: 'Montserrat', sans-serif; }
                .glow-cian { box-shadow: 0 0 20px rgba(6, 182, 212, 0.2); }
                .glow-blue { box-shadow: 0 0 30px rgba(59, 130, 246, 0.15); }
                .text-gradient { background: linear-gradient(to right, #fff, #94a3b8); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
                .card-hover { transition: transform 0.3s ease, box-shadow 0.3s ease; }
                .card-hover:hover { transform: translateY(-4px); box-shadow: 0 20px 40px rgba(59,130,246,0.1); }
                `}
            </style>

            {/* Navigation */}
            <nav className="fixed top-0 w-full z-50 bg-background-dark/80 backdrop-blur-md border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
                    <div className="flex items-center">
                        <img src="/logo.png" alt="FlowSpec QA" className="h-10 md:h-12 w-auto" />
                    </div>

                    {/* Desktop Nav */}
                    <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-400">
                        <a href="#vision" className="hover:text-white transition-colors">Visión</a>
                        <a href="#proceso" className="hover:text-white transition-colors">Proceso</a>
                        <a href="#automatizacion" className="hover:text-white transition-colors">Automatización</a>
                        <a href="#plataforma" className="hover:text-white transition-colors">Plataforma en Acción</a>
                        <NavLink to="/runner" className="hover:text-white transition-colors text-primary font-semibold">Runner</NavLink>
                        <button
                            onClick={() => openContactModal('header_nav')}
                            className="px-5 py-2.5 bg-primary hover:bg-primary-hover rounded-xl shadow-lg shadow-primary/25 transition-all text-white font-bold"
                        >
                            Agendar Consultoría
                        </button>
                    </div>

                    {/* Mobile Hamburger */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="md:hidden p-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
                        aria-label="Toggle menu"
                    >
                        {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="md:hidden border-t border-white/5 bg-background-dark/95 backdrop-blur-xl">
                        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-4">
                            <a href="#vision" onClick={() => setIsMobileMenuOpen(false)} className="text-slate-400 hover:text-white transition-colors py-2 text-sm font-medium">Visión</a>
                            <a href="#proceso" onClick={() => setIsMobileMenuOpen(false)} className="text-slate-400 hover:text-white transition-colors py-2 text-sm font-medium">Proceso</a>
                            <a href="#automatizacion" onClick={() => setIsMobileMenuOpen(false)} className="text-slate-400 hover:text-white transition-colors py-2 text-sm font-medium">Automatización</a>
                            <NavLink to="/runner" onClick={() => setIsMobileMenuOpen(false)} className="text-primary hover:text-white transition-colors py-2 text-sm font-semibold">Runner</NavLink>
                            <a href="#automatizacion" onClick={() => setIsMobileMenuOpen(false)} className="text-slate-400 hover:text-white transition-colors py-2 text-sm font-medium">Plataforma en Acción</a>
                            <div className="border-t border-white/5 pt-4">
                                <button
                                    onClick={() => openContactModal('header_nav_mobile')}
                                    className="w-full px-4 py-3 bg-primary hover:bg-primary-hover rounded-xl font-bold transition-all"
                                >
                                    Agendar Consultoría
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="relative pt-40 pb-20 md:pt-48 md:pb-32 px-4 overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none flex justify-center opacity-25">
                    <div className="relative w-[1200px] mt-20">
                        <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-transparent to-transparent z-10"></div>
                        <img
                            src="/dashboard-mockup.png"
                            alt="FlowSpec QA Interface"
                            className="w-full rounded-xl shadow-2xl shadow-primary/30 border border-white/10"
                        />
                    </div>
                </div>

                <div className="max-w-5xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold mb-6 font-montserrat tracking-widest uppercase">
                        <Sparkles className="w-4 h-4" />
                        QA Engineering Excellence
                    </div>

                    <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tight leading-[1.1] font-montserrat">
                        Ingeniería de Pruebas Potenciada por <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-blue-400 to-indigo-400">IA de Grado Bancario.</span>
                    </h1>

                    <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-3xl mx-auto leading-relaxed">
                        Transforme requisitos complejos en escenarios de prueba trazables, precisos y automatizados —
                        para <span className="text-white font-semibold">Web, Mobile y ejecución end-to-end</span> con FlowSpecQA Runner.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <button
                            onClick={() => openContactModal('hero_button')}
                            className="w-full sm:w-auto px-10 py-5 bg-primary hover:bg-primary-hover rounded-2xl flex items-center justify-center font-bold text-lg shadow-2xl shadow-primary/40 transition-all hover:-translate-y-1"
                        >
                            <span className="flex items-center gap-2">Agendar Consultoría Estratégica <ArrowRight className="w-5 h-5" /></span>
                        </button>
                        <button
                            onClick={() => setIsVideoModalOpen(true)}
                            className="w-full sm:w-auto px-10 py-5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl flex items-center justify-center gap-2 font-bold text-lg transition-all group"
                        >
                            <Play className="w-5 h-5 fill-white group-hover:scale-110 transition-transform" />
                            Ver Demo Técnica
                        </button>
                    </div>
                </div>
            </section>

            {/* Metrics / Social Proof */}
            <section className="py-16 px-4 border-y border-white/5 bg-white/[0.02]">
                <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    {[
                        { value: "10x", label: "Más rápido vs. generación manual de test cases", icon: <Zap className="w-5 h-5 text-yellow-400" /> },
                        { value: "~95%", label: "Menos falsos positivos gracias al enfoque Spec-First", icon: <TrendingUp className="w-5 h-5 text-emerald-400" /> },
                        { value: "100%", label: "Trazabilidad desde el requisito hasta el test case", icon: <FileCheck className="w-5 h-5 text-primary" /> },
                        { value: "<2h", label: "De historia de usuario a suite ejecutable", icon: <Clock className="w-5 h-5 text-indigo-400" /> },
                    ].map((stat, i) => (
                        <div key={i} className="flex flex-col items-center gap-2">
                            <div className="mb-1">{stat.icon}</div>
                            <div className="text-3xl md:text-4xl font-black font-montserrat text-white">{stat.value}</div>
                            <div className="text-xs text-slate-500 leading-snug">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Vision Section — BACKUP (original):
            <section id="vision" className="py-24 px-4 border-b border-white/5 bg-background-dark/50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                        <div className="relative">
                            <div className="absolute -inset-4 bg-primary/10 rounded-3xl blur-2xl"></div>
                            <div className="relative p-8 bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-3xl">
                                <Bot className="w-12 h-12 text-primary mb-6" />
                                <h2 className="text-3xl font-bold mb-6 font-montserrat tracking-tight">La unión entre Experiencia QA y Agentes de IA.</h2>
                                <p className="text-slate-400 text-lg leading-relaxed italic mb-8">
                                    "FlowSpec QA no es solo un generador de texto; es la cristalización de años de experiencia liderando equipos de calidad, automatizada mediante arquitecturas RAG para garantizar que la IA razone como un Lead Senior."
                                </p>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0">
                                        <Shield className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="text-xs text-slate-500 uppercase tracking-widest">FlowSpec QA · Founder & QA Strategist</div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold mb-4 font-montserrat">Automatización con Criterio</h3>
                            <p className="text-slate-400 mb-6 leading-relaxed">
                                La IA genérica suele fallar en los detalles que realmente importan: reglas de negocio, tolerancias, ventanas horarias y validaciones. Por eso usamos Spec-First + Quality Gate: ningún caso se genera sin contratos, reglas explícitas y oráculos verificables. El resultado son casos ejecutables por QA, con evidencia y trazabilidad.
                            </p>
                            <ul className="space-y-4">
                                {["Reducción de auditorías fallidas por falta de cobertura.", "Eliminación de falsos positivos en la generación.", "Trazabilidad garantizada desde el requisito al test case."].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-slate-300">
                                        <CheckCircle2 className="w-5 h-5 text-primary shrink-0" /> {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
            END BACKUP */}

            {/* Vision Section — Redesign: 3 pillars */}
            <section id="vision" className="py-24 px-4 border-b border-white/5 bg-background-dark/50">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-black mb-4 tracking-tight font-montserrat">
                            IA que piensa como <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-indigo-400">un Lead QA Senior.</span>
                        </h2>
                        <p className="text-slate-400 max-w-xl mx-auto text-lg">
                            No es generación de texto. Es razonamiento con contexto, criterio y trazabilidad.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Bot className="w-8 h-8 text-primary" />,
                                color: "bg-primary/10 border-primary/20",
                                iconBg: "bg-primary/10",
                                title: "Razona como un Lead Senior",
                                desc: "Nuestra arquitectura RAG inyecta sus reglas de negocio, tolerancias y validaciones directamente en el razonamiento de la IA — no en el prompt.",
                            },
                            {
                                icon: <Shield className="w-8 h-8 text-emerald-400" />,
                                color: "bg-emerald-500/5 border-emerald-500/20",
                                iconBg: "bg-emerald-500/10",
                                title: "Cero Alucinaciones",
                                desc: "Enfoque Spec-First + Quality Gate: ningún caso se genera sin contratos explícitos y oráculos verificables. Resultados predecibles, siempre.",
                            },
                            {
                                icon: <GitBranch className="w-8 h-8 text-indigo-400" />,
                                color: "bg-indigo-500/5 border-indigo-500/20",
                                iconBg: "bg-indigo-500/10",
                                title: "Trazabilidad Garantizada",
                                desc: "Cada test case mantiene su vínculo con el requisito de origen. Audit-ready desde el primer día para banca, retail y fintech.",
                            },
                        ].map((pillar, i) => (
                            <div key={i} className={`p-8 rounded-3xl border card-hover ${pillar.color}`}>
                                <div className={`w-14 h-14 ${pillar.iconBg} rounded-2xl flex items-center justify-center mb-6`}>
                                    {pillar.icon}
                                </div>
                                <h3 className="text-xl font-bold mb-3 font-montserrat text-white">{pillar.title}</h3>
                                <p className="text-slate-400 text-sm leading-relaxed">{pillar.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section id="proceso" className="py-24 px-4 overflow-hidden">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight font-montserrat">El Enfoque High-Tech de FlowSpec</h2>
                        <p className="text-slate-400">Diseñado para la complejidad, no solo para la velocidad.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative items-stretch">
                        <div className="hidden md:block absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-y-1/2"></div>

                        {[
                            {
                                step: "01",
                                title: "Ingesta con Contexto Empresarial",
                                desc: "Conecte sus fuentes (Jira / PDF / Confluence). Nuestra IA no solo lee, entiende sus reglas de negocio mediante RAG privado y seguro.",
                                icon: <Database className="w-8 h-8 text-primary" />,
                                badge: "Seguridad RAG"
                            },
                            {
                                step: "02",
                                title: "Generación de Escenarios Críticos",
                                desc: "Agentes especializados generan casos positivos, negativos y de borde, eliminando el riesgo de alucinaciones mediante razonamiento multi-paso.",
                                icon: <Bot className="w-8 h-8 text-blue-400" />,
                                badge: "IA Agéntica"
                            },
                            {
                                step: "03",
                                title: "Trazabilidad End-to-End",
                                desc: "Sincronice con Zephyr o Gherkin. Cumpla con los estándares de auditoría bancaria y retail desde el primer día.",
                                icon: <GitBranch className="w-8 h-8 text-emerald-400" />,
                                badge: "Audit-Ready"
                            }
                        ].map((s, i) => (
                            <div key={i} className="relative group card-hover">
                                <div className="p-8 bg-white/5 border border-white/10 rounded-3xl relative z-10 hover:bg-white/10 transition-all duration-500 h-full">
                                    <div className="flex items-center justify-between mb-8">
                                        <div className="w-14 h-14 bg-background-dark border border-white/10 rounded-2xl flex items-center justify-center font-black text-2xl text-primary glow-cian">
                                            {s.step}
                                        </div>
                                        <span className="text-[10px] font-bold px-2 py-1 rounded bg-white/5 border border-white/10 text-slate-500 uppercase tracking-widest">{s.badge}</span>
                                    </div>
                                    <div className="mb-6">{s.icon}</div>
                                    <h3 className="text-xl font-bold mb-3 font-montserrat">{s.title}</h3>
                                    <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Automation Capabilities Section */}
            <section id="automatizacion" className="py-24 px-4 bg-background-dark/40 border-y border-white/5">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold mb-6 font-montserrat tracking-widest uppercase">
                            <Sparkles className="w-4 h-4" />
                            Automatización Completa
                        </div>
                        <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight font-montserrat">Cobertura Total: Web, Mobile y Runner</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto">Desde la generación inteligente hasta la ejecución real de pruebas — todo en una sola plataforma.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Web Automation */}
                        <div className="p-8 bg-white/5 border border-white/10 rounded-3xl card-hover group">
                            <div className="w-14 h-14 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center justify-center mb-6">
                                <Monitor className="w-7 h-7 text-blue-400" />
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                <h3 className="text-xl font-bold font-montserrat">Automatización Web</h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 text-blue-400 uppercase tracking-widest">Playwright</span>
                            </div>
                            <p className="text-slate-400 text-sm leading-relaxed mb-6">
                                Generación automática de scripts Playwright en TypeScript a partir de sus historias de usuario. Tests robustos con selectores semánticos, waits inteligentes y patrones Page Object Model.
                            </p>
                            <ul className="space-y-3">
                                {[
                                    "Scripts Playwright / TypeScript listos para ejecutar",
                                    "Cobertura multi-browser (Chrome, Firefox, Safari)",
                                    "Integración con CI/CD pipelines",
                                    "Reportes con capturas de pantalla y trazas",
                                ].map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                                        <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Mobile Automation */}
                        <div className="p-8 bg-white/5 border border-white/10 rounded-3xl card-hover group">
                            <div className="w-14 h-14 bg-purple-500/10 border border-purple-500/20 rounded-2xl flex items-center justify-center mb-6">
                                <Smartphone className="w-7 h-7 text-purple-400" />
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                <h3 className="text-xl font-bold font-montserrat">Automatización Mobile</h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-500/10 border border-purple-500/20 text-purple-400 uppercase tracking-widest">Maestro</span>
                            </div>
                            <p className="text-slate-400 text-sm leading-relaxed mb-6">
                                Flujos de prueba mobile para iOS y Android generados con Maestro. La IA comprende los patrones de navegación móvil y crea tests que reflejan el comportamiento real del usuario.
                            </p>
                            <ul className="space-y-3">
                                {[
                                    "Flujos Maestro YAML para iOS y Android",
                                    "Soporte para apps nativas, híbridas y React Native",
                                    "Detección automática de elementos UI",
                                    "Integración con emuladores y dispositivos reales",
                                ].map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                                        <CheckCircle2 className="w-4 h-4 text-purple-400 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* FlowSpecQA Runner */}
                        <div className="p-8 bg-gradient-to-br from-primary/10 to-indigo-600/10 border border-primary/20 rounded-3xl card-hover group relative overflow-hidden">
                            <div className="absolute top-3 right-3 text-[10px] font-black border border-primary/30 px-2 py-0.5 rounded-full text-primary tracking-tighter animate-pulse">
                                NEW
                            </div>
                            <div className="w-14 h-14 bg-primary/10 border border-primary/20 rounded-2xl flex items-center justify-center mb-6">
                                <Terminal className="w-7 h-7 text-primary" />
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                <h3 className="text-xl font-bold font-montserrat">FlowSpecQA Runner</h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary uppercase tracking-widest">Runner</span>
                            </div>
                            <p className="text-slate-400 text-sm leading-relaxed mb-6">
                                Motor de ejecución nativo integrado en la plataforma. Ejecute, monitoree y analice sus suites de prueba directamente desde FlowSpec — sin configurar infraestructura adicional.
                            </p>
                            <ul className="space-y-3">
                                {[
                                    "Ejecución directa de tests Web y Mobile",
                                    "Dashboard de resultados en tiempo real",
                                    "Historial y comparación de ejecuciones",
                                    "Alertas y reportes automáticos",
                                ].map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* App Screenshots Carousel */}
            <AppCarousel />



            {/* Footer */}
            <footer className="py-20 border-t border-white/5 bg-background-dark">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-12 border-b border-white/5 pb-16">
                        <div className="max-w-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <img src="/favicon.jpg" alt="Logo" className="w-8 h-8 rounded-lg" />
                                <span className="font-bold text-xl font-montserrat">FlowSpec QA</span>
                            </div>
                            <p className="text-sm text-slate-500 leading-relaxed mb-6">
                                La plataforma de ingeniería de pruebas potenciada por IA que entiende su negocio.
                                Automatización Web, Mobile y ejecución con Runner — creado por expertos QA para desafíos críticos.
                            </p>
                        </div>
                        <div className="grid grid-cols-3 gap-12 md:gap-16">
                            <div>
                                <h4 className="font-bold text-white mb-6 uppercase text-[10px] tracking-widest">Producto</h4>
                                <ul className="space-y-4 text-sm text-slate-500">
                                    <li><a href="#proceso" className="hover:text-primary transition-colors">Proceso</a></li>
                                    <li><a href="#automatizacion" className="hover:text-primary transition-colors">Automatización Web</a></li>
                                    <li><a href="#automatizacion" className="hover:text-primary transition-colors">Automatización Mobile</a></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-bold text-white mb-6 uppercase text-[10px] tracking-widest">Contacto</h4>
                                <ul className="space-y-4 text-sm text-slate-500">
                                    <li><a href="mailto:contacto@flowspecqa.com" className="hover:text-primary transition-colors">Soporte</a></li>
                                    <li><a href="mailto:contacto@flowspecqa.com" className="hover:text-primary transition-colors">Contacto Directo</a></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-bold text-white mb-6 uppercase text-[10px] tracking-widest">Legal</h4>
                                <ul className="space-y-4 text-sm text-slate-500">
                                    <li><NavLink to="/privacy" className="hover:text-primary transition-colors">Privacidad</NavLink></li>
                                    <li><NavLink to="/security" className="hover:text-primary transition-colors">Security First</NavLink></li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row justify-between items-center pt-8 gap-8">
                        <p className="text-[10px] text-slate-600 uppercase tracking-widest">© 2026 IAFlows. FlowSpec QA. Strategic Quality Engineering.</p>
                        <div className="flex gap-4 grayscale opacity-40">
                            <div className="flex items-center gap-2 border border-white/20 px-3 py-1 rounded">
                                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                                <span className="text-[10px] font-bold">ALL SYSTEMS LIVE</span>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>

            <ContactModal
                key={isContactModalOpen ? 'open' : 'closed'}
                isOpen={isContactModalOpen}
                onClose={() => setIsContactModalOpen(false)}
                source={contactSource}
            />
            <VideoModal
                isOpen={isVideoModalOpen}
                onClose={() => setIsVideoModalOpen(false)}
                videoSrc="/demo.mp4"
            />
        </div>
    );
};

export default Landing;
